import type { IntegrationMarket, IntegrationStatus } from './api'

// Shared presentation for the trading sheet's status taxonomy. The sheet
// uses ✅ / 🟡 / 🔵 / X; the portal keeps the same green / amber / blue
// reading but as mono glyphs so chips sit inside the brutalist palette.
export const STATUS_META: Record<IntegrationStatus, { label: string, glyph: string, cls: string }> = {
  live: { label: 'LIVE', glyph: '●', cls: 'is-live' },
  qa: { label: 'QA', glyph: '◐', cls: 'is-qa' },
  test: { label: 'TEST', glyph: '○', cls: 'is-test' },
  not_offered: { label: 'NOT OFFERED', glyph: '✕', cls: 'is-off' },
  unknown: { label: 'UNKNOWN', glyph: '?', cls: 'is-unknown' },
}

export const STATUS_ORDER: readonly IntegrationStatus[] = ['live', 'qa', 'test', 'not_offered', 'unknown']

export function countByStatus(markets: IntegrationMarket[]): Record<IntegrationStatus, number> {
  const out: Record<IntegrationStatus, number> = { live: 0, qa: 0, test: 0, not_offered: 0, unknown: 0 }
  for (const m of markets) out[m.status]++
  return out
}

export function MarketChip({ market }: { market: IntegrationMarket }) {
  const meta = STATUS_META[market.status]
  const title = market.review_note ? `${meta.label} — ${market.review_note}` : meta.label
  return (
    <span
      className={`ad-int-chip ${meta.cls}${market.needs_review ? ' is-review' : ''}`}
      title={title}
    >
      <span className="ad-int-chip-glyph" aria-hidden="true">{meta.glyph}</span>
      <span className="ad-int-chip-label">{market.market_label}</span>
      {market.needs_review && (
        <span className="ad-int-chip-flag" role="img" aria-label="needs review">⚠</span>
      )}
    </span>
  )
}

export function StatusLegend() {
  return (
    <div className="ad-int-legend" aria-label="Status legend">
      <span className="ad-int-legend-title">LEGEND</span>
      {STATUS_ORDER.map((s) => {
        const meta = STATUS_META[s]
        return (
          <span key={s} className={`ad-int-chip ${meta.cls}`}>
            <span className="ad-int-chip-glyph" aria-hidden="true">{meta.glyph}</span>
            <span className="ad-int-chip-label">{meta.label}</span>
          </span>
        )
      })}
      <span className="ad-int-chip is-review">
        <span className="ad-int-chip-label">⚠ NEEDS REVIEW</span>
      </span>
    </div>
  )
}

// Review notes under a chip row — one line per flagged market.
export function ReviewNotes({ markets }: { markets: IntegrationMarket[] }) {
  const flagged = markets.filter(m => m.needs_review && m.review_note)
  if (flagged.length === 0) return null
  return (
    <div className="ad-int-reviews">
      {flagged.map(m => (
        <p key={m.market_key} className="ad-int-review">
          ⚠
          {' '}
          <strong>{m.market_label}</strong>
          {' — '}
          {m.review_note}
        </p>
      ))}
    </div>
  )
}
